import React, { useState, useEffect } from 'react';
import { videoRecorderService } from '../../services/videoRecorderService';
import { VideoRecording } from '../../types/videoRecorder';
import VideoPlayerComponent from './VideoPlayerComponent';
import VideoShareComponent from './VideoShareComponent';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { supabase } from '../../config/supabaseClient';
import { generateSubtitles } from '../../services/openaiService';
import AIThumbnailModal from './AIThumbnailModal';

interface VideoGalleryComponentProps {
  onRecordNew?: () => void;
  sharedVideoId?: string;
}

const VideoGalleryComponent: React.FC<VideoGalleryComponentProps> = ({ onRecordNew, sharedVideoId }) => {
  const { user } = useAuth();
  const [isPremium, setIsPremium] = useState<boolean>(false);
  
  // Check premium status from Supabase
  useEffect(() => {
    const checkPremiumStatus = async () => {
      if (!user) return;
      try {
        const { data } = await supabase
          .from('user_subscriptions')
          .select('status')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .maybeSingle();
        setIsPremium(!!data);
      } catch (error) {
        console.error('Error checking premium status:', error);
      }
    };
    
    checkPremiumStatus();
  }, [user]);
  const [videos, setVideos] = useState<VideoRecording[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeVideo, setActiveVideo] = useState<VideoRecording | null>(null);
  const [shareVideoId, setShareVideoId] = useState<string | null>(null);
  const [sortOption, setSortOption] = useState<'date' | 'name' | 'views'>('date');
  const [isGeneratingSubs, setIsGeneratingSubs] = useState<boolean>(false);
  const [thumbUrls, setThumbUrls] = useState<Record<string, string>>({});
  const [aiModalOpen, setAiModalOpen] = useState<boolean>(false);
  const [aiTargetVideo, setAiTargetVideo] = useState<VideoRecording | null>(null);
  
  // Load videos or specific shared video
  useEffect(() => {
    const fetchVideos = async () => {
      if (!user) return;
      
      setIsLoading(true);
      try {
        if (sharedVideoId) {
          // If we have a sharedVideoId, just fetch that specific video
          const video = await videoRecorderService.getVideoById(sharedVideoId);
          if (video) {
            setVideos([video]);
            setActiveVideo(video);
          }
        } else {
          // Otherwise fetch all user videos
          const userVideos = await videoRecorderService.getUserVideos();
          setVideos(userVideos);
        }
      } catch (error) {
        console.error('Error loading videos:', error);
        setError('Failed to load your videos');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchVideos();
  }, [user, sharedVideoId]);

  // Resolve signed URLs for thumbnails whenever the list changes
  useEffect(() => {
    const resolveThumbs = async () => {
      const updates: Record<string, string> = {};
      for (const v of videos) {
        if (v.thumbnail_path && !thumbUrls[v.id]) {
          const url = await videoRecorderService.getThumbnailSignedUrl(v.thumbnail_path, 3600);
          if (url) updates[v.id] = url;
        }
      }
      if (Object.keys(updates).length) {
        setThumbUrls(prev => ({ ...prev, ...updates }));
      }
    };
    if (videos.length) resolveThumbs();
  }, [videos, thumbUrls]);
  
  const handleDeleteVideo = async (videoId: string) => {
    // Use window.confirm instead of global confirm to avoid ESLint no-restricted-globals error
    if (!window.confirm('Are you sure you want to delete this video? This action cannot be undone.')) {
      return;
    }
    
    try {
      const success = await videoRecorderService.deleteVideo(videoId);
      
      if (success) {
        setVideos(prev => prev.filter(v => v.id !== videoId));
        
        // If the deleted video was active, clear it
        if (activeVideo && activeVideo.id === videoId) {
          setActiveVideo(null);
        }
        
        // If sharing panel was open for this video, close it
        if (shareVideoId === videoId) {
          setShareVideoId(null);
        }
        
        toast.success('Video deleted successfully');
      } else {
        throw new Error('Failed to delete video');
      }
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Failed to delete video');
    }
  };
  
  const handleUpdateTitle = async (videoId: string, newTitle: string) => {
    if (!newTitle.trim()) {
      toast.error('Title cannot be empty');
      return;
    }
    
    try {
      const updatedVideo = await videoRecorderService.updateVideo(
        videoId,
        { title: newTitle }
      );
      
      if (updatedVideo) {
        setVideos(prev => 
          prev.map(v => v.id === videoId ? updatedVideo : v)
        );
        
        // Update active video if it's the one being edited
        if (activeVideo && activeVideo.id === videoId) {
          setActiveVideo(updatedVideo);
        }
        
        toast.success('Title updated successfully');
      } else {
        throw new Error('Failed to update title');
      }
    } catch (error) {
      console.error('Error updating title:', error);
      toast.error('Failed to update title');
    }
  };
  
  const sortVideos = (videos: VideoRecording[]) => {
    return [...videos].sort((a, b) => {
      switch (sortOption) {
        case 'date':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'name':
          return a.title.localeCompare(b.title);
        case 'views':
          return (b.view_count || 0) - (a.view_count || 0);
        default:
          return 0;
      }
    });
  };
  
  const sortedVideos = sortVideos(videos);

  // Subtitle handlers
  const refreshVideoInLists = (updated: VideoRecording) => {
    setVideos(prev => prev.map(v => (v.id === updated.id ? updated : v)));
    if (activeVideo && activeVideo.id === updated.id) {
      setActiveVideo(updated);
    }
  };

  const openAIThumbnail = (video: VideoRecording) => {
    if (!isPremium) {
      toast('AI thumbnails are a premium feature.', { icon: '✨' });
      return;
    }
    setAiTargetVideo(video);
    setAiModalOpen(true);
  };

  const handleGenerateSubtitles = async (video: VideoRecording) => {
    try {
      setIsGeneratingSubs(true);
      const res = await generateSubtitles(video.id);
      let subtitlePath = res.subtitlePath || null;
      if (!subtitlePath && res.vtt) {
        const saved = await videoRecorderService.saveSubtitleFromText(video.id, res.vtt);
        subtitlePath = saved;
      }
      if (subtitlePath) {
        const updated = { ...video, subtitle_path: subtitlePath } as VideoRecording;
        refreshVideoInLists(updated);
        toast.success('Subtitles generated');
      } else {
        throw new Error('Subtitle generation returned no result');
      }
    } catch (e) {
      console.error('Generate subtitles error:', e);
      toast.error(e instanceof Error ? e.message : 'Failed to generate subtitles');
    } finally {
      setIsGeneratingSubs(false);
    }
  };

  const handleUploadSubtitle = async (video: VideoRecording) => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.vtt,text/vtt';
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        const path = await videoRecorderService.uploadSubtitleFromFile(video.id, file);
        if (path) {
          const updated = { ...video, subtitle_path: path } as VideoRecording;
          refreshVideoInLists(updated);
          toast.success('Subtitle uploaded');
        } else {
          toast.error('Failed to upload subtitle');
        }
      };
      input.click();
    } catch (e) {
      console.error('Upload subtitle error:', e);
      toast.error('Failed to upload subtitle');
    }
  };

  const handleEditSubtitle = async (video: VideoRecording) => {
    try {
      const vtt = prompt('Paste/enter WebVTT content (must start with "WEBVTT")', 'WEBVTT\n\n');
      if (vtt === null) return;
      const path = await videoRecorderService.saveSubtitleFromText(video.id, vtt);
      if (path) {
        const updated = { ...video, subtitle_path: path } as VideoRecording;
        refreshVideoInLists(updated);
        toast.success('Subtitle saved');
      } else {
        toast.error('Failed to save subtitle');
      }
    } catch (e) {
      console.error('Edit subtitle error:', e);
      toast.error('Failed to save subtitle');
    }
  };

  const handleRemoveSubtitle = async (video: VideoRecording) => {
    try {
      const ok = window.confirm('Remove subtitles from this video?');
      if (!ok) return;
      const success = await videoRecorderService.deleteSubtitle(video.id);
      if (success) {
        const updated = { ...video, subtitle_path: null } as VideoRecording;
        refreshVideoInLists(updated);
        toast.success('Subtitle removed');
      } else {
        toast.error('Failed to remove subtitle');
      }
    } catch (e) {
      console.error('Remove subtitle error:', e);
      toast.error('Failed to remove subtitle');
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-12">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-gray-700 text-lg">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
        >
          Retry
        </button>
      </div>
    );
  }
  
  if (videos.length === 0) {
    return (
      <div className="text-center py-12">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
        <h3 className="text-lg font-medium text-gray-700 mb-2">No videos yet</h3>
        <p className="text-gray-600 mb-6">Start by recording your first video</p>
        
        {onRecordNew && (
          <button
            onClick={onRecordNew}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow flex items-center justify-center mx-auto"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
            </svg>
            Record New Video
          </button>
        )}
      {/* AI Thumbnail Modal */}
      {aiModalOpen && aiTargetVideo && (
        <AIThumbnailModal
          open={aiModalOpen}
          onClose={() => setAiModalOpen(false)}
          video={aiTargetVideo}
          onApplied={async ({ thumbnailPath, signedUrl }) => {
            try {
              const updated = { ...aiTargetVideo, thumbnail_path: thumbnailPath } as VideoRecording;
              refreshVideoInLists(updated);
              const url = signedUrl || await videoRecorderService.getThumbnailSignedUrl(thumbnailPath, 3600);
              if (url) setThumbUrls(prev => ({ ...prev, [aiTargetVideo.id]: url }));
            } catch (e) {
              console.error('Post-apply thumbnail error:', e);
            }
          }}
        />
      )}
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-1 gap-6">
      {/* Video list - full width grid */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">Your Videos</h2>
          
          <div className="flex items-center space-x-3">
            <div className="flex items-center">
              <label htmlFor="sort" className="sr-only">Sort by</label>
              <select
                id="sort"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value as any)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="date">Newest</option>
                <option value="name">Name</option>
                <option value="views">Most viewed</option>
              </select>
            </div>
            
            {onRecordNew && (
              <button
                onClick={onRecordNew}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                </svg>
                Record New
              </button>
            )}
          </div>
        </div>
        
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, idx) => (
              <div key={idx} className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
                <div className="relative bg-gray-200" style={{ paddingTop: '56.25%' }} />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                  <div className="h-6 bg-gray-200 rounded w-24 mt-2" />
                </div>
              </div>
            ))}
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg shadow-sm">
            <div className="mx-auto h-12 w-12 text-gray-300">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" viewBox="0 0 24 24" fill="currentColor"><path d="M4 5a2 2 0 00-2 2v10a2 2 0 002 2h7v-2H4V9h16v4h2V7a2 2 0 00-2-2H4z"/><path d="M19 14a1 1 0 00-1 1v2h-2a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2v-2a1 1 0 00-1-1z"/></svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-800">No videos yet</h3>
            <p className="mt-1 text-sm text-gray-600">Record your first screen or camera video to get started.</p>
            {onRecordNew && (
              <button onClick={onRecordNew} className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow">Record a video</button>
            )}
          </div>
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {sortedVideos.map(video => (
            <div 
              key={video.id} 
              className={`bg-white rounded-lg shadow-sm overflow-hidden ${
                activeVideo && activeVideo.id === video.id ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              <div className="grid grid-cols-1 gap-6">
                <div 
                  className="col-span-12 cursor-pointer"
                  onClick={() => setActiveVideo(video)}
                >
                  <div className="relative bg-gray-100 rounded-lg overflow-hidden" style={{ paddingTop: '56.25%' }}>
                    {video.thumbnail_path && thumbUrls[video.id] ? (
                      <img 
                        src={thumbUrls[video.id]!}
                        alt={video.title}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          // Retry once with cache-busting to mitigate transient QUIC/HTTP3 failures
                          if (!img.dataset.retry) {
                            img.dataset.retry = '1';
                            const sep = img.src.includes('?') ? '&' : '?';
                            img.src = img.src + sep + 'cb=' + Date.now();
                            return;
                          }
                          img.style.display = 'none';
                          const parent = img.parentElement;
                          if (parent) {
                            parent.innerHTML = `
                              <div class="absolute inset-0 w-full h-full flex items-center justify-center bg-gray-200">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </div>
                            `;
                          }
                        }}
                      />
                    ) : (
                      <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gray-200">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    
                    {video.duration_seconds && (
                      <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white px-1.5 py-0.5 rounded text-xs">
                        {Math.floor(video.duration_seconds / 60)}:{(video.duration_seconds % 60).toString().padStart(2, '0')}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="col-span-12 p-3">
                  <div 
                    className="font-medium text-gray-800 mb-1 cursor-pointer hover:text-blue-600"
                    onClick={() => setActiveVideo(video)}
                  >
                    {video.title || 'Untitled Video'}
                  </div>
                  
                  <div className="text-sm text-gray-500 mb-2">
                    {new Date(video.created_at).toLocaleDateString()} • {video.view_count || 0} views
                  </div>
                  
                  <div className="flex space-x-2 mt-2">
                    <button
                      onClick={() => setShareVideoId(video.id)}
                      className="px-2 py-1 text-xs flex items-center text-blue-600 hover:text-blue-800"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                      </svg>
                      Share
                    </button>

                    <button
                      onClick={() => openAIThumbnail(video)}
                      className={`px-2 py-1 text-xs flex items-center ${isPremium ? 'text-indigo-600 hover:text-indigo-800' : 'text-gray-400 cursor-not-allowed'}`}
                      title={isPremium ? 'Generate AI thumbnail' : 'Premium feature'}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M11 17a1 1 0 11-2 0v-1H7a1 1 0 01-.894-1.447l1-2A1 1 0 018 12h1V8H8a1 1 0 01-.894-1.447l1-2A1 1 0 019 4h2a1 1 0 01.894.553l1 2A1 1 0 0112 8h-1v4h1a1 1 0 01.894.553l1 2A1 1 0 0113 16h-2v1z" />
                      </svg>
                      AI Thumbnail
                    </button>
                    
                    <button
                      onClick={() => {
                        const newTitle = prompt('Enter new title', video.title);
                        if (newTitle !== null) {
                          handleUpdateTitle(video.id, newTitle);
                        }
                      }}
                      className="px-2 py-1 text-xs flex items-center text-gray-600 hover:text-gray-800"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                      Rename
                    </button>
                    
                    <button
                      onClick={() => handleDeleteVideo(video.id)}
                      className="px-2 py-1 text-xs flex items-center text-red-600 hover:text-red-800"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Sharing panel */}
              {shareVideoId === video.id && (
                <div className="border-t border-gray-200">
                  <VideoShareComponent 
                    videoId={video.id}
                    onClose={() => setShareVideoId(null)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        )}

        {/* Close wrapper for the video list section */}
        </div>

      {/* Active video player stacked below */}
      {activeVideo && (
        <div className="space-y-4">
  <VideoPlayerComponent video={activeVideo} controls={true} />
  
  <div className="bg-white rounded-lg shadow-sm p-4">
    <h3 className="text-lg font-medium text-gray-800 mb-2">{activeVideo.title || 'Untitled Video'}</h3>
    <div className="flex justify-between items-center text-sm text-gray-500">
      <div>
        {new Date(activeVideo.created_at).toLocaleDateString()} • {activeVideo.view_count || 0} views
      </div>
      <div className="flex space-x-4">
        <button
          onClick={() => setShareVideoId(activeVideo.id === shareVideoId ? null : activeVideo.id)}
          className="flex items-center hover:text-blue-600"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
          </svg>
          Share
        </button>
        <button
          onClick={() => openAIThumbnail(activeVideo)}
          className={`flex items-center ${isPremium ? 'hover:text-indigo-600' : 'text-gray-400 cursor-not-allowed'}`}
          title={isPremium ? 'Generate AI thumbnail' : 'Premium feature'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path d="M11 17a1 1 0 11-2 0v-1H7a1 1 0 01-.894-1.447l1-2A1 1 0 018 12h1V8H8a1 1 0 01-.894-1.447l1-2A1 1 0 019 4h2a1 1 0 01.894.553l1 2A1 1 0 0112 8h-1v4h1a1 1 0 01.894.553l1 2A1 1 0 0113 16h-2v1z" />
          </svg>
          AI Thumbnail
        </button>
        <button
          onClick={() => {
            const newTitle = prompt('Enter new title', activeVideo.title);
            if (newTitle !== null) {
              handleUpdateTitle(activeVideo.id, newTitle);
            }
          }}
          className="flex items-center hover:text-gray-800"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
          Rename
        </button>
      </div>
    </div>

    {/* Subtitles actions */}
    <div className="flex items-center space-x-3">
      <button
        disabled={isGeneratingSubs}
        onClick={() => handleGenerateSubtitles(activeVideo)}
        className={`flex items-center ${isGeneratingSubs ? 'opacity-60 cursor-not-allowed' : 'hover:text-green-600'}`}
        title="Generate subtitles with Whisper"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path d="M2 5a2 2 0 012-2h4l2 2h6a2 2 0 012 2v2h-2V7H4v10h6v2H4a2 2 0 01-2-2V5z" />
        </svg>
        {isGeneratingSubs ? 'Generating…' : 'Generate Subs'}
      </button>
      <button
        onClick={() => handleUploadSubtitle(activeVideo)}
        className="flex items-center hover:text-indigo-600"
        title="Upload .vtt subtitle file"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path d="M3 3a2 2 0 012-2h6l4 4v2h-2V6H9V3H5v14h5v2H5a2 2 0 01-2-2V3z" />
        </svg>
        Upload Subs
      </button>
      <button
        onClick={() => handleEditSubtitle(activeVideo)}
        className="flex items-center hover:text-yellow-600"
        title="Edit subtitles as VTT"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path d="M17.414 2.586a2 2 0 010 2.828l-9.9 9.9L4 16l.686-3.514 9.9-9.9a2 2 0 012.828 0z" />
        </svg>
        Edit Subs
      </button>
      {activeVideo.subtitle_path && (
        <button
          onClick={() => handleRemoveSubtitle(activeVideo)}
          className="flex items-center text-red-500 hover:text-red-700"
          title="Remove subtitles"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Remove Subs
        </button>
      )}
    </div>

    {/* Delete action */}
    <button
      onClick={() => handleDeleteVideo(activeVideo.id)}
      className="flex items-center text-red-500 hover:text-red-700"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      Delete
    </button>
            
            {activeVideo.description && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-1">Description</h4>
                <p className="text-gray-600 whitespace-pre-wrap">{activeVideo.description}</p>
              </div>
            )}
          </div>
          
          {shareVideoId === activeVideo.id && (
            <div className="bg-white rounded-lg shadow-sm">
              <VideoShareComponent 
                videoId={activeVideo.id}
                onClose={() => setShareVideoId(null)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoGalleryComponent;
